import dayjs from 'dayjs'
import type { PromoterIntegrationTask } from '../../../api/promoter-integration-task/types'
import type { FormattedPromoterTask } from './types'
import { kebabToTitleCase } from '../../../utils/helpers'

function generateClickThrough(task: PromoterIntegrationTask): string {
  if (task.messageListOid) {
    return `/message-center/lists/${task.messageListOid}/contacts`
  }
  else if (task.messageTaskOid) {
    return `/message-center/messages/${task.messageTaskOid}/view`
  }
  else if (task.campaignOid) {
    return `/campaigns/view/${task.campaignOid}/dashboard`
  }
  else if (task.eventOid) {
    return `/events/${task.eventOid}/view/sales`
  }
  else if (task.name === 'fan-message') {
    if (task.provider === 'sms') {
      return `/message-center/messages/view/${task.oid}/recipients`
    }
    return `/message-center/messages/view/${task.oid}/overview`
  }
  else if (task.name == 'merge-tags') {
    return '/audience/tags'
  }
  return '/audience'
}

function taskStartDateFormatter(taskStartedDate: string): string {
  const taskStartedObj = dayjs(taskStartedDate)
  return taskStartedObj.month() > 1 && taskStartedObj.month() < 8
    ? taskStartedObj.format('h:mm a, MMMM D, YYYY')
    : taskStartedObj.format('h:mm a, MMM. D, YYYY')
}

const importTypeMap: Record<string, string[]> = {
  'audience-contact': ['List import', 'List importing', 'List imported'],
  'audience-sales': ['Audience import', 'Audience importing', 'Audience imported'],
  'audience': ['Ticket sales import', 'Ticket sales importing', 'Ticket sales imported'],
  'event': ['Events import', 'Events importing', 'Events imported'],
  'event-and-audience': ['Events & tickets import', 'Events & tickets importing', 'Events & tickets imported'],
  'product-order': ['POS import', 'POS orders importing', 'POS orders imported'],
  'event-and-product-order': ['Events & POS import', 'Events & POS importing', 'Events & POS imported'],
}

function formatTask(task: PromoterIntegrationTask): FormattedPromoterTask {
  const type = task.name
  let name = kebabToTitleCase(type)
  let icon = 'edit'
  const clickthrough = generateClickThrough(task)
  const isCompleted = task.status === 'completed'
  const subtitle = isCompleted
    ? `Completed ${taskStartDateFormatter(task.sysMtime)}`
    : `Started ${taskStartDateFormatter(task.started)}`
  const taskStatusSuffix = isCompleted ? 'ed' : 'ing'
  const fanCount = task.statusDetails?.fanOids?.length
  const displayFanCount = fanCount ? fanCount : ''
  const fanCountSuffix = fanCount && fanCount > 1 ? 's' : ''
  // use pattern below
  // name = `Edit${taskStatusSuffix} ${displayFanCount} contact${fanCountSuffix}`;
  if (type.includes('background') && type?.includes('import')) {
    const fileName = task.importFilename || ''
    const assetType = task.importAssetType
    const importTypeOptions = assetType && assetType in importTypeMap ? importTypeMap[assetType] : ['Import', 'Importing', 'Imported']
    const importText = isCompleted ? importTypeOptions[2] : importTypeOptions[1]
    name = `${fileName.length >= 20 ? fileName.substring(0, 20) + '...' : fileName} - ${importText}`
    icon = 'upload'
  }
  else if (type === 'mass-edit-tags') {
    name = `Edit${taskStatusSuffix} contact tags`
    icon = 'edit'
  }
  // FIXME which branch?
  else if (type === 'mass-edit-tags') {
    name = 'Mass edit contact tags'
    icon = 'edit'
  }
  else if (type === 'background-event-import-csv') {
    name = 'Mass events import'
    icon = 'upload'
  }
  else if (type === 'mass-edit-fan-attributes') {
    // the statusDetails contains the fanOids. But it is null now.
    name = `Edit${taskStatusSuffix} ${displayFanCount} contact${fanCountSuffix}`
    icon = 'edit'
  }
  else if (type === 'mass-edit-message-list') {
    name = `Edit${taskStatusSuffix} list subscriptions`
    icon = 'edit'
  }
  else if (type === 'mass-delete-fans') {
    // cspell:ignore Delet
    name = `Delet${taskStatusSuffix} ${displayFanCount} contact${fanCountSuffix}`
    icon = 'trash'
  }
  else if (type === 'fan-message') {
    const messageSubject = task.messageSubject ?? ''
    name = `${messageSubject} - email sent`
    icon = 'email'
  }
  else if (type === 'fan-export-csv') {
    const totalFanSynced = task.statusDetails?.totalFanSynched || 0
    name = `Export${taskStatusSuffix} ${totalFanSynced ? totalFanSynced : ''} contact${totalFanSynced > 1 ? 's' : ''}`
    icon = 'export'
  }
  else if (type === 'message-list-oid-fan-export-csv') {
    name = `Export${taskStatusSuffix} contacts from list`
    icon = 'export'
  }
  else if (type === 'audience-filter-fan-export-csv') {
    name = `Export${taskStatusSuffix} contacts`
    icon = 'export'
  }
  else if (type === 'message-task-oid-fan-export-csv') {
    name = `Export${taskStatusSuffix} message recipients`
    icon = 'export'
  }
  else if (type === 'loyalty-programs-data-synch') {
    const provider = task.provider && task.provider?.length > 0 ? task.provider.charAt(0).toUpperCase() + task.provider.slice(1) : 'loyalty program'
    name = `Data sync${taskStatusSuffix} from ${provider}`
    icon = 'audience-alt'
  }
  else if (type === 'event-data-synch') {
    const provider = task.provider && task.provider?.length > 0 ? task.provider.charAt(0).toUpperCase() + task.provider.slice(1) : 'integration'
    name = `Data sync${taskStatusSuffix} from ${provider}`
    icon = 'audience-alt'
  }
  else if (type === 'fans-to-custom-audience-synch') {
    name = `Facebook Custom Audience sync${taskStatusSuffix}`
    icon = 'audience-alt'
  }
  else if (type === 'delete-event-pos-orders') {
    name = `Clear${taskStatusSuffix} event POS orders`
    icon = 'trash'
  }
  else if (type === 'delete-event-ticket-sales') {
    name = `Clear${taskStatusSuffix} event ticket sales`
    icon = 'trash'
  }
  return {
    name,
    subtitle,
    type,
    icon,
    clickthrough,
    startTime: task.started,
    status: task.status,
  }
}

export function formatTasks(tasks: PromoterIntegrationTask[]) {
  return tasks.map(formatTask)
}

export function pendingTasksOfResourceAndOid(pendingTasks: PromoterIntegrationTask[], resource: keyof PromoterIntegrationTask, oid: number) {
  return pendingTasks.filter(item => item[resource] === oid)
}

export function pendingTaskOfTypeAndResourceAndOid(pendingTasks: PromoterIntegrationTask[], type: string, resource: keyof PromoterIntegrationTask, oid: number) {
  return pendingTasks.filter(item => item.name === type && item[resource] === oid)
}
